<div class="content-wrapper">
    <!--
        HEADER ROW: Bezeichnung | Date col 1 [In %] | Date col 2 [In %] | … | Gesamt Δ
        - Bilanz scope → balance-sheet date (DD.MM.YYYY)
        - Erfolgsrechnung scope → period (DD.MM.YYYY – DD.MM.YYYY)
        - Rendered via DateLocalePipe so they follow the active i18n locale.
        - The short year still drives the year-pills above; the table header itself shows
          only the long date to keep the column self-explanatory.
    -->
    <div class="table-header-row" [style.gridTemplateColumns]="gridTemplateColumns">
        <div class="header-label">{{ 'aumico_analytics_table_label_column' | i18next }}</div>
        <ng-container *ngFor="let year of yearColumnLabels; let i = index">
            <div class="header-label text-end" [class.col-blue]="i % 2 === 0">
                <ng-container *ngIf="controls.reportScope === 'bilanz'">
                    <ng-container *ngIf="yearColumnPeriods[i]?.balanceSheetDate as bsDate; else fallbackYear">
                        {{ bsDate | dateLocale }}
                    </ng-container>
                </ng-container>
                <ng-container *ngIf="controls.reportScope === 'erfolg'">
                    <ng-container *ngIf="yearColumnPeriods[i] as period; else fallbackYear">
                        <ng-container *ngIf="period.incomeFromDate && period.incomeToDate; else fallbackYear">
                            <span class="header-period">
                                <span class="header-period__from">{{ period.incomeFromDate | dateLocale }}</span>
                                <span class="header-period__to">– {{ period.incomeToDate | dateLocale }}</span>
                            </span>
                        </ng-container>
                    </ng-container>
                </ng-container>
                <ng-template #fallbackYear>{{ formatYearLabel(year) }}</ng-template>
            </div>
            <div *ngIf="controls.structMode" class="header-label text-end" [class.col-blue]="i % 2 === 0">
                {{ 'aumico_analytics_table_struct_column' | i18next }}
            </div>
        </ng-container>
        <div class="header-label text-end">{{ 'aumico_analytics_table_delta_column' | i18next }}</div>
    </div>

    <div class="table-body">
        <ng-container *ngFor="let row of rows; trackBy: trackByRow">
            <!-- GAP ROW between sections -->
            <div *ngIf="row.kind === 'gap'" class="section-gap"></div>

            <!-- TREE ROW (group / FLI / sub-FLI / of-that / subtotal / ER-total) -->
            <ng-container *ngIf="row.kind === 'tree'">
                <div
                    class="grid-row"
                    [ngClass]="{
                        'section-header-row': row.level === 0 && !row.isErTotal,
                        'er-total-row': row.isErTotal,
                        'group-row': row.level > 0 && row.nodeType === 'GROUP',
                        'fli-row': row.nodeType === 'FLI',
                        'sub-fli-row': row.nodeType === 'SUB_FLI' || row.nodeType === 'OF_THAT_ITEM',
                        'subtotal-row': row.nodeType === 'SUB_TOTAL',
                        'is-reference': row.isReference
                    }"
                    [style.gridTemplateColumns]="gridTemplateColumns"
                    [style.--level]="row.level"
                    [attr.role]="row.isExpandable ? 'button' : null"
                    [attr.tabindex]="row.isExpandable ? 0 : null"
                    [attr.aria-expanded]="row.isExpandable ? row.isExpanded : null"
                    (click)="onRowClick(row)"
                    (keydown.enter)="onRowClick(row)"
                    (keydown.space)="onRowClick(row); $event.preventDefault()">
                    <div class="col-name">
                        <span
                            *ngIf="row.isExpandable"
                            class="ham-icon chevron"
                            [class.expanded]="row.isExpanded">
                            <i class="chevron-right-icon"></i>
                        </span>
                        <!-- `title` attribute = browser tooltip on hover, useful when
                             long labels get truncated to "…" by the SCSS `text-overflow: ellipsis`. -->
                        <span class="title" [attr.title]="row.label">{{ row.label }}</span>
                    </div>

                    <ng-container *ngFor="let cell of row.values">
                        <!--
                            Always wrap the value in a <span>. The bare span is invisible for
                            normal rows; for subtotal / ER-total rows the SCSS targets
                            `.subtotal-row .col-val > span` / `.er-total-row .col-val > span`
                            to paint the single-top + double-bottom border. This avoids the
                            old conditional `<ng-template #plainValue>` duplication.
                        -->
                        <div class="col-val" [class.col-blue]="cell.columnIndex % 2 === 0">
                            <span>{{ cell.display }}</span>
                        </div>
                        <div *ngIf="controls.structMode" class="col-val col-struct" [class.col-blue]="cell.columnIndex % 2 === 0">
                            {{ cell.structPct }}
                        </div>
                    </ng-container>

                    <div class="col-val col-delta">
                        <ng-container
                            *ngTemplateOutlet="deltaBadge; context: { delta: row.delta }">
                        </ng-container>
                    </div>
                </div>
            </ng-container>

            <!-- ACCOUNT ROW -->
            <div
                *ngIf="row.kind === 'account'"
                class="grid-row account-row"
                [style.gridTemplateColumns]="gridTemplateColumns"
                [style.--level]="row.level">
                <div class="col-name">
                    <!-- Synthetic accounts (Gewinn / Verlust / shortfall) have an empty
                         accountNumber. Mirror PDFTron and just render the name in that case. -->
                    <span *ngIf="row.accountNumber" class="acc-number">{{ row.accountNumber }}</span>
                    <span class="acc-name" [attr.title]="row.label">{{ row.label }}</span>
                </div>

                <ng-container *ngFor="let cell of row.values">
                    <div class="col-val" [class.col-blue]="cell.columnIndex % 2 === 0">{{ cell.display }}</div>
                    <div *ngIf="controls.structMode" class="col-val col-struct" [class.col-blue]="cell.columnIndex % 2 === 0">
                        {{ cell.structPct }}
                    </div>
                </ng-container>

                <div class="col-val col-delta">
                    <ng-container
                        *ngTemplateOutlet="deltaBadge; context: { delta: row.delta }">
                    </ng-container>
                </div>
            </div>
        </ng-container>

        <div *ngIf="rows.length === 0" class="myc-empty-after-filter">
            {{ 'aumico_analytics_table_empty_after_filter' | i18next }}
        </div>
    </div>

    <!--
        Δ badge:
          - Bilanz rows + flat ER rows  → grey outline (neutral)
          - ER good (revenue ↑ / expense ↓) → green
          - ER bad  (revenue ↓ / expense ↑) → red
          - n/a (oldest = 0) → plain "-" text — no badge background
    -->
    <ng-template #deltaBadge let-delta="delta">
        <!--
            Note: we intentionally do NOT set [disabled] — that would apply opacity: 0.5
            to the whole badge and wash out the colour. Visual interactivity is suppressed
            via `pointer-events: none` in SCSS instead, since these badges are read-only
            labels (no click action).
        -->
        <ham-badge-button
            *ngIf="delta.classification !== 'na'; else naText"
            [variant]="badgeVariantFor(delta.classification)">
            {{ delta.display }}
        </ham-badge-button>
        <ng-template #naText>
            <span class="delta-na">{{ delta.display }}</span>
        </ng-template>
    </ng-template>
</div>
